import React, { useEffect, useRef, useState } from "react";
import { Tooltip, Popover, Input, Button, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import posthog from "posthog-js";
import {
    REGISTER_POSTHOG_API_KEY,
    POSTHOG_API_HOST,
} from "../../../../env";
import {
    buildDefaultRedirectURL,
    readUtmSource,
} from "../../../../utils/signupHandoff";
import { encodePrefillPayload } from "../../../../utils/heroUpload";
import { buildPortalSelections } from "./heroSelectionMappers";
import ASSETS from "../../assets.json";
import AddAssetModal from "./AddAssetModal";
import "./HeroPromptBar.scss";

const ASSET_TYPES = [
    { key: "image",   label: "Image",   icon: ASSETS.hero.iconImage,   iconSize: 16,
      tooltip: "Brand-aligned images from a prompt or inspiration image." },
    { key: "video",   label: "Video",   icon: ASSETS.hero.iconVideo,   iconSize: 20,
      tooltip: "Brand-aligned videos generated from a product photo and prompt." },
    { key: "spatial", label: "Spatial 3D", icon: ASSETS.hero.iconSpatial, iconSize: 20,disabled: true,
      tooltip: "True-to-scale images from structured 3D models. Currently in beta — sign up to join the waitlist." },
    { key: "bulk",    label: "Project/Bulk", icon: ASSETS.hero.iconBulk, iconSize: 16, disabled: true,
      tooltip: "You do not have free credits for bulk upload" },
];

const VIDEO_DURATIONS_BY_MODEL = {
    "Veo 3":    ["4s", "6s", "8s"],
    "Gen 4.5":  ["5s", "10s"],
    "Seedance": ["4s", "6s", "8s", "10s", "15s"],
};

const INFLUENCER_PROMPT_LIMITS = {
    "Veo 3":    { maxDurationSeconds: 8,  maxWords: 23 },
    "Gen 4.5":  { maxDurationSeconds: 10, maxWords: 23 },
    "Seedance": { maxDurationSeconds: 15, maxWords: 50 },
};

const parseDurationSeconds = (label) => {
    const n = parseInt(String(label || "").trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const countWords = (text) => {
    const trimmed = String(text || "").trim();
    return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
};

const getInfluencerWordLimit = (modelLabel, durationLabel) => {
    const cfg = INFLUENCER_PROMPT_LIMITS[modelLabel] || INFLUENCER_PROMPT_LIMITS["Seedance"];
    const parsed = parseDurationSeconds(durationLabel) ?? cfg.maxDurationSeconds;
    const bounded = Math.min(Math.max(parsed, 1), cfg.maxDurationSeconds);
    return Math.max(1, Math.ceil((bounded / cfg.maxDurationSeconds) * cfg.maxWords));
};

const SHOT_TYPE_LABELS = ["Lifestyle", "Studio / Silo", "Product Swap", "Dimension Overlays"];
const VARIANT_LOCKED_SHOT = "Preview Image";

const MODE_CONTROLS = {
    image: [
        { key: "category",    label: "Category",  options: ["Product", "Variant"], highlight: true },
        {
            key: "shotType",
            label: "Shot Type",
            options: (sel) => sel?.category === "Variant" ? [VARIANT_LOCKED_SHOT] : SHOT_TYPE_LABELS,
            locked: (sel) => sel?.category === "Variant",
        },
        {
            key: "model",
            label: "AI Model",
            options: (sel) => {
                if (sel?.category === "Variant") return ["Nano Banana Pro"];
                if (sel?.shotType === "Dimension Overlays") return ["ChatGPT 2"];
                return ["Recommended", "Nano Banana Pro", "ChatGPT 2", "Flux"];
            },
            locked: (sel) => sel?.category === "Variant" || sel?.shotType === "Dimension Overlays",
        },
        { key: "orientation", label: "Orientation", options: ["Square", "Landscape", "Portrait"] },
        { key: "count",       label: "Images",    options: ["1 Image", "2 Images", "3 Images", "4 Images"] },
    ],
    video: [
        { key: "category",   label: "Category",   options: ["Product"], highlight: true, locked: true },
        { key: "videoMode",  label: "Mode",       options: ["Custom Video", "360° Spin", "Influencer Video"] },
        {
            key: "model",
            label: "Model",
            options: (sel) => sel?.videoMode === "Influencer Video"
                ? ["Veo 3", "Seedance"]
                : ["Veo 3", "Gen 4.5", "Seedance"],
        },
        { key: "duration",   label: "Duration",   options: (sel) => VIDEO_DURATIONS_BY_MODEL[sel?.model] || ["4s", "6s", "8s"] },
        { key: "resolution", label: "Resolution", options: ["720p", "1080p"] },
    ],
    spatial: [
        { key: "type",  label: "Type",     options: ["Scene", "Product"], highlight: true },
        {
            key: "model",
            label: "3D Model",
            options: (sel) => sel?.type === "Scene" ? ["ALL3D"] : ["Tripo", "Trellis2"],
            locked: (sel) => sel?.type === "Scene",
        },
    ],
};

const DEFAULT_SELECTIONS = {
    image:   { model: "Recommended", category: "Product", shotType: "Lifestyle", orientation: "Square", count: "4 Images" },
    video:   { category: "Product", videoMode: "Custom Video", model: "Veo 3", duration: "4s", resolution: "720p" },
    spatial: { type: "Scene", model: "Tripo" },
};

const getPromptPlaceholder = (activeType, selections) => {
    if (activeType === "image") {
        const imgSel = selections.image || {};
        if (imgSel.category === "Variant") return "Describe a variant, upload a material reference image, or choose a hex color";
        switch (imgSel.shotType) {
            case "Studio / Silo":        return "Describe your shot (e.g., 'on marble') or upload inspiration for background reference. Leave blank for a standard white background silo with contact shadows.";
            case "Product Swap":         return "Optional — describe how to adapt the scene (e.g. 'but make the walls green')";
            case "Dimension Overlays":   return "Add additional dimension information here...";
            case "Lifestyle":
            default:                      return "Describe an image or upload an inspiration image and click generate...";
        }
    }
    if (activeType === "video") {
        switch (selections.video?.videoMode) {
            case "360° Spin":        return "Describe the 360° spin...";
            case "Influencer Video": return "Optional: write the influencer script, or leave blank and we'll generate the ad copy.";
            default:                  return "Describe a video and click generate...";
        }
    }
    if (activeType === "spatial") {
        return "Describe a product or upload an inspiration image and click generate...";
    }
    return "";
};

const trackHeroIntent = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const email     = urlParams.get("email") || "";
        let utm_source  = readUtmSource();

        const ph = posthog.init(REGISTER_POSTHOG_API_KEY, {
            api_host: POSTHOG_API_HOST,
            autocapture: false,
            name: "register",
            capture_pageview: false,
            disable_session_recording: true,
            loaded: (instance) => {
                if (utm_source) instance.register({ utm_source });
            },
        });
        if (!utm_source) utm_source = "direct";
        if (ph) {
            ph.capture("free_trial_click", {
                location: window.location.pathname,
                button:   "try_ai_images_for_free",
                utm_source,
                email,
            });
        }
    } catch {
        // PostHog must never block signup.
    }
};

const HeroPromptBar = ({ onRequestSignup, modalBackdropZIndex = 10001 }) => {
    const [activeType, setActiveType] = useState("image");
    const [prompt, setPrompt] = useState("");
    const [selections, setSelections] = useState(DEFAULT_SELECTIONS);
    const [openControl, setOpenControl] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("product");
    const [modalCommitted, setModalCommitted] = useState(null);

    const [productAdded, setProductAdded] = useState(false);
    const [productUploads, setProductUploads] = useState([]);
    const [productMeta, setProductMeta] = useState(null);
    const [inspirationUpload, setInspirationUpload] = useState(null);
    const [propUploads, setPropUploads] = useState([]);

    const [variantHexCode, setVariantHexCode] = useState("");
    const [variantSize, setVariantSize] = useState("");
    const [hexPopoverOpen, setHexPopoverOpen] = useState(false);
    const [hexDraft, setHexDraft] = useState("");
    const [sizePopoverOpen, setSizePopoverOpen] = useState(false);
    const [sizeDraft, setSizeDraft] = useState("");

    const controlsRef = useRef(null);

    useEffect(() => {
        if (!openControl) return;
        const onDown = (e) => {
            if (controlsRef.current && !controlsRef.current.contains(e.target)) {
                setOpenControl(null);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [openControl]);

    useEffect(() => { setOpenControl(null); }, [activeType]);

    useEffect(() => {
        const inVariant = activeType === "image" && selections.image?.category === "Variant";
        if (!inVariant) {
            if (variantHexCode) setVariantHexCode("");
            if (variantSize) setVariantSize("");
        }
    }, [activeType, selections.image?.category]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeType !== "image") return;
        const shot = selections.image.shotType;
        if (shot === "Dimension Overlays") {
            if (selections.image.count !== "1 Image" || selections.image.model !== "ChatGPT 2") {
                setSelections((prev) => ({
                    ...prev,
                    image: { ...prev.image, count: "1 Image", model: "ChatGPT 2" },
                }));
            }
        } else if (selections.image.category === "Product"
                && (selections.image.count === "1 Image" || selections.image.model === "ChatGPT 2")) {
            setSelections((prev) => ({
                ...prev,
                image: { ...prev.image, count: "4 Images", model: "Recommended" },
            }));
        }
    }, [activeType, selections.image.shotType]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeSelections = selections[activeType] || {};
    const imageCategory = selections.image?.category || "Product";
    const imageShotType = selections.image?.shotType || "Lifestyle";

    const activeControls = (MODE_CONTROLS[activeType] || [])
        .map((ctl) => ({
            ...ctl,
            options: typeof ctl.options === "function" ? ctl.options(activeSelections) : ctl.options,
            locked: typeof ctl.locked === "function" ? ctl.locked(activeSelections) : !!ctl.locked,
        }));

    const isVariantMode = activeType === "image" && imageCategory === "Variant";
    const showInspirationSlot =
        (activeType === "image" && imageCategory === "Product" && (imageShotType === "Lifestyle" || imageShotType === "Studio / Silo" || imageShotType === "Product Swap")) ||
        (activeType === "image" && imageCategory === "Variant") ||
        (activeType === "spatial" && activeSelections.type === "Scene");
    const showPropSlot =
        (activeType === "image" && imageCategory === "Product" && imageShotType !== "Dimension Overlays") ||
        (activeType === "spatial" && activeSelections.type === "Scene");
    const inspirationNoun = isVariantMode
        ? "material reference"
        : imageShotType === "Product Swap"
            ? "scene reference"
            : "inspiration";
    const inspirationCardLabel = `Add ${inspirationNoun}`;
    const replaceInspirationLabel = `Replace ${inspirationNoun}`;

    const isInfluencer = activeType === "video" && selections.video?.videoMode === "Influencer Video";
    const influencerLimit = isInfluencer
        ? getInfluencerWordLimit(selections.video?.model, selections.video?.duration)
        : null;
    const influencerWordCount = isInfluencer ? countWords(prompt) : 0;
    const overInfluencerLimit = isInfluencer && influencerWordCount > influencerLimit;
    const generateDisabled =
        (!prompt.trim() && !inspirationUpload) || overInfluencerLimit;

    const updateSelection = (controlKey, value) => {
        setSelections((prev) => {
            const nextForMode = { ...prev[activeType], [controlKey]: value };
            if (activeType === "video" && controlKey === "model") {
                const allowed = VIDEO_DURATIONS_BY_MODEL[value] || [];
                if (!allowed.includes(nextForMode.duration)) {
                    nextForMode.duration = allowed[allowed.length - 1] || nextForMode.duration;
                }
            }
            if (activeType === "video" && controlKey === "videoMode") {
                if (value === "Influencer Video") {
                    nextForMode.model = "Seedance";
                    nextForMode.duration = "15s";
                }
            }
            if (activeType === "image" && controlKey === "category") {
                if (value === "Variant") {
                    nextForMode.shotType = VARIANT_LOCKED_SHOT;
                    nextForMode.count = "2 Images";
                    nextForMode.model = "Nano Banana Pro";
                } else {
                    nextForMode.shotType = "Lifestyle";
                    nextForMode.model = "Recommended";
                }
            }
            return { ...prev, [activeType]: nextForMode };
        });
        if (activeType === "video" && controlKey === "videoMode") {
            if (value === "Influencer Video" || value === "Custom Video") {
                setPrompt("");
            } else if (value === "360° Spin") {
                setPrompt("Generate 360 spin video of the product.");
            }
        }
        setOpenControl(null);
    };

    const handleSelectAssetType = (key, isDisabled) => {
        if (isDisabled) return;
        setActiveType(key);
    };

    const openModalIn = (mode, committed = null) => {
        setModalMode(mode);
        setModalCommitted(committed);
        setModalOpen(true);
    };

    const handleAddProductClick = () => {
        openModalIn(
            "product",
            productAdded
                ? {
                      uploads: productUploads,
                      primaryFileUid: productMeta?.primaryFileUid,
                      width: productMeta?.width,
                      height: productMeta?.height,
                      depth: productMeta?.depth,
                      unit: productMeta?.unit,
                      name: productMeta?.name,
                      tags: productMeta?.tags,
                  }
                : null
        );
    };

    const handleAddInspirationClick = () => openModalIn("inspiration");

    const handleAddPropClick = () => {
        if (propUploads.length >= 3) {
            message.info("You can add up to 3 props.");
            return;
        }
        openModalIn("prop");
    };

    const handleModalSubmit = (result) => {
        if (result.mode === "product") {
            setProductUploads(result.uploads);
            setProductMeta({
                width: result.width,
                height: result.height,
                depth: result.depth,
                unit: result.unit,
                name: result.name,
                tags: result.tags,
                primaryFileUid: result.primaryFileUid,
            });
            setProductAdded(true);
        } else if (result.mode === "inspiration") {
            const first = result.uploads[0];
            setInspirationUpload({
                uid: first.uid,
                name: first.name,
                image_uri: first.image_uri,
                url: first.url,
                source: result.source || "upload",
                library: first.library || null,
                meta: { name: result.name, tags: result.tags },
            });
        } else if (result.mode === "prop") {
            commitPropFromResult(result);
        }
        setModalOpen(false);
    };

    const commitPropFromResult = (result) => {
        const first = result.uploads[0];
        setPropUploads((prev) => [
            ...prev,
            {
                uid: first.uid,
                name: first.name,
                image_uri: first.image_uri,
                url: first.url,
                images: result.uploads,
                primaryFileUid: result.primaryFileUid,
                meta: {
                    name: result.name,
                    tags: result.tags,
                    width: result.width,
                    height: result.height,
                    depth: result.depth,
                    unit: result.unit,
                },
            },
        ]);
    };

    const handleModalAddAnother = (result) => {
        if (result.mode !== "prop") return;
        commitPropFromResult(result);
        if (propUploads.length + 1 >= 3) {
            setModalOpen(false);
        }
    };

    const handleRemoveProduct = () => {
        setProductAdded(false);
        setProductUploads([]);
        setProductMeta(null);
        setOpenControl(null);
        setInspirationUpload(null);
        setPropUploads([]);
    };

    const buildAiCreatorRedirectURL = () => {
        const utm_source = readUtmSource() || "direct";
        const doneUploads = productUploads.filter((u) => u.status === "done" && u.image_uri);

        if (!productAdded || doneUploads.length === 0) {
            return buildDefaultRedirectURL(utm_source);
        }

        const imgCategory = selections.image?.category || "Product";
        const imgShotType = selections.image?.shotType || "Lifestyle";
        const allowInspiration =
            (activeType === "image" && imgCategory === "Product" && (imgShotType === "Lifestyle" || imgShotType === "Studio / Silo" || imgShotType === "Product Swap")) ||
            (activeType === "spatial" && selections.spatial?.type === "Scene");
        const allowProps =
            (activeType === "image" && imgCategory === "Product" && imgShotType !== "Dimension Overlays") ||
            (activeType === "spatial" && selections.spatial?.type === "Scene");

        const inspiration =
            allowInspiration && inspirationUpload && inspirationUpload.image_uri
                ? {
                      uid: inspirationUpload.uid,
                      name: inspirationUpload.name,
                      image_uri: inspirationUpload.image_uri,
                      url: inspirationUpload.url,
                      source: inspirationUpload.source || "upload",
                      library: inspirationUpload.library || null,
                      meta: inspirationUpload.meta || null,
                  }
                : null;

        const props = allowProps
            ? propUploads
                  .filter((p) => p.image_uri)
                  .map((p) => ({
                      uid: p.uid,
                      name: p.name,
                      image_uri: p.image_uri,
                      url: p.url,
                      images: (p.images || []).filter((i) => i.image_uri).map((i) => ({ uid: i.uid, name: i.name, image_uri: i.image_uri, url: i.url })),
                      primaryFileUid: p.primaryFileUid || null,
                      meta: p.meta || null,
                  }))
            : [];

        const SHOT_TYPE_TO_KEY = {
            "Lifestyle": "lifestyle",
            "Studio / Silo": "studio",
            "Product Swap": "scene_lock",
            "Dimension Overlays": "dimension_lines",
        };
        const imageModeType = imgCategory === "Variant" ? "product_variant" : (SHOT_TYPE_TO_KEY[imgShotType] || "lifestyle");

        const variantInVariantMode = activeType === "image" && imgCategory === "Variant";

        const payload = {
            v: 1,
            create_mode: activeType,
            image_mode_type: activeType === "image" ? imageModeType : null,
            prompt: prompt || "",
            product: {
                images: doneUploads.map((u) => ({ uid: u.uid, name: u.name, image_uri: u.image_uri, url: u.url })),
                primaryFileUid: productMeta?.primaryFileUid || doneUploads[0].uid,
                name: productMeta?.name || "Untitled Product",
                tags: productMeta?.tags || [],
                dimensions: {
                    width: productMeta?.width ?? 0,
                    height: productMeta?.height ?? 0,
                    depth: productMeta?.depth ?? 0,
                    unit: productMeta?.unit || "inches",
                },
            },
            inspiration,
            props,
            variant: variantInVariantMode
                ? {
                      hex_code: variantHexCode || null,
                      size: variantSize || null,
                      material_reference: inspiration,
                  }
                : null,
            selections: buildPortalSelections(activeType, selections[activeType] || {}),
            utm_source,
        };

        const encoded = encodePrefillPayload(payload);
        const params = new URLSearchParams();
        params.set("stage", "upload_images");
        params.set("create_mode", activeType);
        if (payload.image_mode_type) params.set("image_mode_type", payload.image_mode_type);
        if (payload.prompt) params.set("prompt", payload.prompt);
        params.set("prefill", encoded);
        params.set("utm_source", utm_source);
        return `/ai-asset-creator?${params.toString()}`;
    };

    const handleGenerate = () => {
        trackHeroIntent();
        onRequestSignup(buildAiCreatorRedirectURL());
    };

    const renderAssetCard = (img, label, badgeText, onRemove) => (
        <div className="lv2-asset-card">
            {img ? <img src={img} alt={label} /> : null}
            {badgeText && <span className="lv2-asset-card-badge manrope f-11 w-600">{badgeText}</span>}
            <button type="button" className="lv2-asset-card-remove" aria-label={`Remove ${label}`} onClick={onRemove}>
                <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
            </button>
        </div>
    );

    const renderAddCard = (label, onClick) => (
        <button type="button" className="lv2-asset-card-add manrope f-11 w-500" onClick={onClick}>
            <span className="lv2-asset-card-plus">
                <svg width="14" height="14" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.78558 1.57227H7.71415C7.61891 1.57227 7.57129 1.61988 7.57129 1.71512V7.32227H2.25028C2.15504 7.32227 2.10742 7.36988 2.10742 7.46512V8.53655C2.10742 8.63179 2.15504 8.67941 2.25028 8.67941H7.57129V14.2866C7.57129 14.3818 7.61891 14.4294 7.71415 14.4294H8.78558C8.88081 14.4294 8.92843 14.3818 8.92843 14.2866V8.67941H14.2503C14.3455 8.67941 14.3931 8.63179 14.3931 8.53655V7.46512C14.3931 7.36988 14.3455 7.32227 14.2503 7.32227H8.92843V1.71512C8.92843 1.61988 8.88081 1.57227 8.78558 1.57227Z" fill="currentColor"/>
                </svg>
            </span>
            <span>{label}</span>
        </button>
    );

    return (
        <>
            <div className={`lv2-input-bar ${modalOpen ? "is-dimmed" : ""} ${productAdded ? "has-product" : "no-prompt"}`}>
                <div className="lv2-asset-tabs">
                    {ASSET_TYPES.map((type) => {
                        const isActive = activeType === type.key;
                        const isDisabled = type.disabled;
                        const btn = (
                            <button
                                key={type.key}
                                type="button"
                                data-attr={`hero-asset-tab-${type.key}`}
                                className={`lv2-asset-tab ${isActive ? "active" : ""} ${isDisabled ? "is-disabled" : ""}`}
                                onClick={() => handleSelectAssetType(type.key, isDisabled)}
                                disabled={isDisabled}
                                aria-disabled={isDisabled || undefined}
                            >
                                {type.icon && (
                                    <img src={type.icon} alt={type.label} style={{ width: type.iconSize, height: type.iconSize }} />
                                )}
                                <span className="manrope f-14 w-500 lh-22 white">{type.label}</span>
                            </button>
                        );
                        return type.tooltip ? (
                            <Tooltip key={type.key} title={type.tooltip} overlayClassName="lv2-tooltip">
                                <span className="lv2-asset-tab-wrap">{btn}</span>
                            </Tooltip>
                        ) : (
                            btn
                        );
                    })}
                </div>

                {productAdded && (
                    <div className="lv2-asset-row">
                        <div className="lv2-asset-card">
                            {productUploads[0]?.url ? <img src={productUploads[0].url} alt={productUploads[0].name || "Product"} /> : null}
                            {productUploads.length > 1 && (
                                <span className="lv2-asset-card-badge manrope f-11 w-600">+{productUploads.length - 1}</span>
                            )}
                            <button type="button" className="lv2-asset-card-remove" aria-label="Remove product" onClick={handleRemoveProduct}>
                                <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                            </button>
                        </div>

                        {showPropSlot && propUploads.map((p, idx) => (
                            <div key={p.uid + idx} className="lv2-asset-card">
                                {p.url ? <img src={p.url} alt={p.name} /> : null}
                                {(p.images?.length || 0) > 1 && (
                                    <span className="lv2-asset-card-badge manrope f-11 w-600">+{p.images.length - 1}</span>
                                )}
                                <button
                                    type="button"
                                    className="lv2-asset-card-remove"
                                    aria-label="Remove prop"
                                    onClick={() => setPropUploads((prev) => prev.filter((_, i) => i !== idx))}
                                >
                                    <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                                </button>
                            </div>
                        ))}
                        {!isVariantMode && showInspirationSlot && inspirationUpload && (
                            renderAssetCard(inspirationUpload.url, inspirationUpload.name, null, () => setInspirationUpload(null))
                        )}

                        {showPropSlot && propUploads.length < 3 && renderAddCard("Add prop or more products", handleAddPropClick)}

                        {!isVariantMode && showInspirationSlot && renderAddCard(
                            inspirationUpload ? replaceInspirationLabel : inspirationCardLabel,
                            handleAddInspirationClick
                        )}

                        {isVariantMode && showInspirationSlot && inspirationUpload && (
                            renderAssetCard(inspirationUpload.url, inspirationUpload.name, null, () => setInspirationUpload(null))
                        )}

                        {isVariantMode && showInspirationSlot && renderAddCard(
                            inspirationUpload ? replaceInspirationLabel : inspirationCardLabel,
                            handleAddInspirationClick
                        )}

                        {isVariantMode && (
                            <>
                                <Popover
                                    trigger="click"
                                    placement="top"
                                    open={hexPopoverOpen}
                                    onOpenChange={(open) => {
                                        setHexPopoverOpen(open);
                                        if (open) setHexDraft(variantHexCode || "");
                                    }}
                                    overlayClassName="lv2-variant-popover"
                                    zIndex={5500}
                                    content={
                                        <div className="lv2-variant-popover-inner">
                                            <input
                                                type="color"
                                                value={hexDraft && /^#[0-9A-Fa-f]{6}$/.test(hexDraft) ? hexDraft : "#FFFFFF"}
                                                onChange={(e) => setHexDraft(e.target.value.toUpperCase())}
                                                className="lv2-variant-color-swatch"
                                                aria-label="Pick variant colour"
                                            />
                                            <Input
                                                placeholder="#FF0000"
                                                value={hexDraft}
                                                onChange={(e) => setHexDraft(e.target.value)}
                                                onPressEnter={() => {
                                                    const normalised = hexDraft.trim().startsWith("#") ? hexDraft.trim().toUpperCase() : `#${hexDraft.trim().toUpperCase()}`;
                                                    setVariantHexCode(/^#[0-9A-Fa-f]{6}$/.test(normalised) ? normalised : "");
                                                    setHexPopoverOpen(false);
                                                }}
                                                style={{ width: 120 }}
                                            />
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => {
                                                    const normalised = hexDraft.trim().startsWith("#") ? hexDraft.trim().toUpperCase() : `#${hexDraft.trim().toUpperCase()}`;
                                                    setVariantHexCode(/^#[0-9A-Fa-f]{6}$/.test(normalised) ? normalised : "");
                                                    setHexPopoverOpen(false);
                                                }}
                                                aria-label="Apply hex code"
                                            >
                                                →
                                            </Button>
                                        </div>
                                    }
                                >
                                    <div className={`lv2-asset-card-variant ${variantHexCode ? "is-filled" : ""}`}>
                                        {variantHexCode ? (
                                            <>
                                                <span className="lv2-variant-swatch" style={{ background: variantHexCode }} />
                                                <span className="lv2-variant-text manrope f-12 w-600">{variantHexCode}</span>
                                                <button
                                                    type="button"
                                                    className="lv2-asset-card-remove"
                                                    aria-label="Clear colour"
                                                    onClick={(e) => { e.stopPropagation(); setVariantHexCode(""); }}
                                                >
                                                    <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="lv2-asset-card-plus">
                                                    <svg width="14" height="14" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8.78558 1.57227H7.71415C7.61891 1.57227 7.57129 1.61988 7.57129 1.71512V7.32227H2.25028C2.15504 7.32227 2.10742 7.36988 2.10742 7.46512V8.53655C2.10742 8.63179 2.15504 8.67941 2.25028 8.67941H7.57129V14.2866C7.57129 14.3818 7.61891 14.4294 7.71415 14.4294H8.78558C8.88081 14.4294 8.92843 14.3818 8.92843 14.2866V8.67941H14.2503C14.3455 8.67941 14.3931 8.63179 14.3931 8.53655V7.46512C14.3931 7.36988 14.3455 7.32227 14.2503 7.32227H8.92843V1.71512C8.92843 1.61988 8.88081 1.57227 8.78558 1.57227Z" fill="currentColor"/>
                                                    </svg>
                                                </span>
                                                <span className="manrope f-11 w-500">Pick hex color</span>
                                            </>
                                        )}
                                    </div>
                                </Popover>

                                <Popover
                                    trigger="click"
                                    placement="top"
                                    open={sizePopoverOpen}
                                    onOpenChange={(open) => {
                                        setSizePopoverOpen(open);
                                        if (open) setSizeDraft(variantSize || "");
                                    }}
                                    overlayClassName="lv2-variant-popover"
                                    zIndex={5500}
                                    content={
                                        <div className="lv2-variant-popover-inner">
                                            <Input
                                                autoFocus
                                                placeholder="e.g. M, 12oz, Large"
                                                value={sizeDraft}
                                                onChange={(e) => setSizeDraft(e.target.value)}
                                                onPressEnter={() => {
                                                    setVariantSize(sizeDraft.trim());
                                                    setSizePopoverOpen(false);
                                                }}
                                                style={{ width: 180 }}
                                            />
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => { setVariantSize(sizeDraft.trim()); setSizePopoverOpen(false); }}
                                                aria-label="Apply size"
                                            >
                                                →
                                            </Button>
                                        </div>
                                    }
                                >
                                    <div className={`lv2-asset-card-variant ${variantSize ? "is-filled" : ""}`}>
                                        {variantSize ? (
                                            <>
                                                <span className="lv2-variant-text manrope f-13 w-600">{variantSize}</span>
                                                <span className="manrope f-11 w-500 lv2-variant-sublabel">Size</span>
                                                <button
                                                    type="button"
                                                    className="lv2-asset-card-remove"
                                                    aria-label="Clear size"
                                                    onClick={(e) => { e.stopPropagation(); setVariantSize(""); }}
                                                >
                                                    <DeleteOutlined style={{ color: "#ffffff", fontSize: 12 }} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="lv2-asset-card-plus">
                                                    <svg width="14" height="14" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8.78558 1.57227H7.71415C7.61891 1.57227 7.57129 1.61988 7.57129 1.71512V7.32227H2.25028C2.15504 7.32227 2.10742 7.36988 2.10742 7.46512V8.53655C2.10742 8.63179 2.15504 8.67941 2.25028 8.67941H7.57129V14.2866C7.57129 14.3818 7.61891 14.4294 7.71415 14.4294H8.78558C8.88081 14.4294 8.92843 14.3818 8.92843 14.2866V8.67941H14.2503C14.3455 8.67941 14.3931 8.63179 14.3931 8.53655V7.46512C14.3931 7.36988 14.3455 7.32227 14.2503 7.32227H8.92843V1.71512C8.92843 1.61988 8.88081 1.57227 8.78558 1.57227Z" fill="currentColor"/>
                                                    </svg>
                                                </span>
                                                <span className="manrope f-11 w-500">Add size (optional)</span>
                                            </>
                                        )}
                                    </div>
                                </Popover>
                            </>
                        )}

                    </div>
                )}

                {productAdded && (
                    <>
                        <textarea
                            className="lv2-prompt-textarea manrope f-14 w-400 lh-22 white"
                            placeholder={getPromptPlaceholder(activeType, selections)}
                            value={prompt}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (isInfluencer && countWords(next) > influencerLimit) return;
                                setPrompt(next);
                            }}
                            rows={2}
                        />
                        {isInfluencer && (
                            <div className={`lv2-prompt-wordcount manrope f-12 ${overInfluencerLimit ? "is-over" : ""}`}>
                                {influencerWordCount}/{influencerLimit} words
                            </div>
                        )}
                    </>
                )}

                <div className="lv2-action-row">
                    {!productAdded ? (
                        <button
                            type="button"
                            data-attr="hero-add-product"
                            className="lv2-add-product-btn manrope f-14 w-500 lh-22 white"
                            onClick={handleAddProductClick}
                        >
                            <svg width="14" height="14" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.78558 1.57227H7.71415C7.61891 1.57227 7.57129 1.61988 7.57129 1.71512V7.32227H2.25028C2.15504 7.32227 2.10742 7.36988 2.10742 7.46512V8.53655C2.10742 8.63179 2.15504 8.67941 2.25028 8.67941H7.57129V14.2866C7.57129 14.3818 7.61891 14.4294 7.71415 14.4294H8.78558C8.88081 14.4294 8.92843 14.3818 8.92843 14.2866V8.67941H14.2503C14.3455 8.67941 14.3931 8.63179 14.3931 8.53655V7.46512C14.3931 7.36988 14.3455 7.32227 14.2503 7.32227H8.92843V1.71512C8.92843 1.61988 8.88081 1.57227 8.78558 1.57227Z" fill="currentColor"/>
                            </svg>
                            <span>Add a product</span>
                        </button>
                    ) : (
                        <div className="lv2-controls" ref={controlsRef}>
                            {activeControls.map((ctl) => {
                                const value = (selections[activeType] || {})[ctl.key] || (ctl.options[0] ?? "");
                                const isOpen = openControl === ctl.key;
                                const isLocked = !!ctl.locked;
                                return (
                                    <div key={ctl.key} className="lv2-control">
                                        <button
                                            type="button"
                                            data-attr={`hero-control-${activeType}-${ctl.key}`}
                                            className={`lv2-control-btn manrope w-500 lh-22 white ${isOpen ? "open" : ""} ${ctl.highlight ? "is-highlighted" : ""} ${isLocked ? "is-locked" : ""}`}
                                            onClick={isLocked ? undefined : () => setOpenControl(isOpen ? null : ctl.key)}
                                            aria-label={ctl.label}
                                            aria-disabled={isLocked || undefined}
                                        >
                                            <span className="lv2-control-value">{value}</span>
                                            {!isLocked && (
                                                <svg className="lv2-control-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </button>
                                        {!isLocked && isOpen && (
                                            <div className="lv2-control-menu" role="menu">
                                                {ctl.options.map((opt) => (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        role="menuitem"
                                                        className={`lv2-control-option manrope w-500 lh-22 white ${opt === value ? "selected" : ""}`}
                                                        onClick={() => updateSelection(ctl.key, opt)}
                                                    >
                                                        <span>{opt}</span>
                                                        {opt === value && (
                                                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <Tooltip
                        title={generateDisabled ? "Add product photo and prompt/inspiration to continue" : ""}
                        overlayClassName="lv2-tooltip"
                        zIndex={6000}
                    >
                        <span className="lv2-generate-btn-wrap">
                            <button
                                type="button"
                                data-attr="hero-start-free"
                                className="lv2-generate-btn manrope f-14 w-500 lh-22 white"
                                onClick={handleGenerate}
                                disabled={generateDisabled}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor"/>
                                    <path d="M19 15L19.75 17.25L22 18L19.75 18.75L19 21L18.25 18.75L16 18L18.25 17.25L19 15Z" fill="currentColor"/>
                                </svg>
                                <span>Generate</span>
                            </button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            <AddAssetModal
                open={modalOpen}
                mode={modalMode}
                title={isVariantMode && modalMode === "inspiration" ? "Add Material Reference Image" : undefined}
                committed={modalCommitted}
                onCancel={() => setModalOpen(false)}
                onSubmit={handleModalSubmit}
                backdropZIndex={modalBackdropZIndex}
                allowAll3dLibrary={!isVariantMode}
                currentPropCount={propUploads.length}
                maxProps={3}
                onAddAnother={handleModalAddAnother}
            />
        </>
    );
};

export default HeroPromptBar;
